import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import {
  Typography,
  FormControl,
  FormLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Button,
  IconButton,
  FormGroup,
  CircularProgress,
} from "@mui/material";
import { Box } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Select from "@mui/material/Select";
import "./searchOption.scss";
import { useNavigate } from "react-router-dom";
import { DEFAULT_FILTER } from "../../store/constants";
import { fetchPaymentMethods } from "../../lib/helper";

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

// Payment methods will be loaded dynamically from API

const SearchOptions = ({
  setFilterValues,
  routeDetails,
  filterValues,
  setPosition,
  setSourceCity,
}) => {
  const navigate = useNavigate();
  const [isFormVisible, setFormVisible] = useState(false);
  const [paymentValue, setPaymentValue] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(true);

  // Load payment methods from API on component mount
  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        setIsLoadingPaymentMethods(true);
        const apiResponse = await fetchPaymentMethods();
        // Extract the values from the API response (the display names)
        const methods = Object.values(apiResponse);
        setPaymentMethods(methods);
      } catch (error) {
        console.error("Failed to load payment methods:", error);
        // Fallback to empty array if API fails
        setPaymentMethods([]);
      } finally {
        setIsLoadingPaymentMethods(false);
      }
    };

    loadPaymentMethods();
  }, []);

  const toggleFormVisibility = () => {
    setFormVisible(!isFormVisible);
  };

  const handleChange = (event) => {
    const {
      target: { value },
    } = event;

    let newValues;
    if (value.includes("Select all")) {
      if (paymentValue.length === paymentMethods.length) {
        newValues = [];
      } else {
        newValues = paymentMethods;
      }
    } else {
      newValues = value.includes("Select all")
        ? paymentMethods
        : value.filter((item) => item !== "Select all");
    }

    setPaymentValue(newValues);

    // Apply filter immediately
    setFilterValues({
      ...filterValues,
      paymentTypes: newValues,
    });
  };

  const formik = useFormik({
    initialValues: DEFAULT_FILTER,
    onSubmit: (values) => {
      setPosition(null);
      setSourceCity(null);
      setFilterValues({ ...values, paymentTypes: paymentValue });
    },
  });

  const handleReset = () => {
    setPaymentValue([]);
    formik.resetForm({
      values: {
        fuelType: [],
        vehicleSize: "",
        stationStatus: ["Operational"],
        hours: [],
        paymentTypes: [],
        source: [],
      },
    });

    // Also reset your state separately if needed
    setFilterValues({
      fuelType: [],
      vehicleSize: "",
      stationStatus: ["Operational"],
      hours: [],
      paymentTypes: [],
      source: [],
    });
  };

  console.log("🚀 ~ SearchOptions ~ formik:", formik?.values);

  return (
    <Box>
      <Box className="search-option-main">
        <Box className="search-option-header">
          <Typography variant="h1" className="search-option-text">
            Search options
          </Typography>
          <IconButton onClick={toggleFormVisibility} sx={{ color: "#08652e" }}>
            {isFormVisible ? <RemoveIcon /> : <AddIcon />}
          </IconButton>
        </Box>

        {isFormVisible && (
          <form onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
            <FormControl
              component="fieldset"
              sx={{ marginTop: "2px !important" }}
            >
              <FormLabel component="legend">Fuel type</FormLabel>
              <FormGroup>
                {["CNG", "LNG"].map((type) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        checked={filterValues.fuelType.includes(type)}
                        onChange={() => {
                          const currentFuelType = filterValues.fuelType || [];
                          const isChecked = currentFuelType.includes(type);

                          const updatedFuelType = isChecked
                            ? currentFuelType.filter((item) => item !== type)
                            : [...currentFuelType, type];

                          setFilterValues({
                            ...filterValues,
                            fuelType: updatedFuelType,
                          });

                          formik.setFieldValue("fuelType", updatedFuelType);
                        }}
                      />
                    }
                    label={<Typography variant="body2">{type}</Typography>}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">Fuel source</FormLabel>
              <FormGroup>
                {["Renewable (RNG)"].map((source) => (
                  <FormControlLabel
                    key={source}
                    control={
                      <Checkbox
                        checked={filterValues.source.includes(source)}
                        onChange={() => {
                          const currentSource = filterValues.source || [];
                          const isChecked = currentSource.includes(source);

                          const updatedSource = isChecked
                            ? currentSource.filter((item) => item !== source)
                            : [...currentSource, source];

                          setFilterValues({
                            ...filterValues,
                            source: updatedSource,
                          });

                          formik.setFieldValue("source", updatedSource);
                        }}
                      />
                    }
                    label={<Typography variant="body2">{source}</Typography>}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">Vehicle size</FormLabel>
              <RadioGroup
                value={filterValues.vehicleSize || ""}
                onChange={(event) => {
                  const selectedSize = event.target.value;
                  setFilterValues({
                    ...filterValues,
                    vehicleSize: selectedSize,
                  });
                  formik.setFieldValue("vehicleSize", selectedSize);
                }}
              >
                {["Cars and vans", "Box trucks", "Semi trucks"].map((size) => (
                  <FormControlLabel
                    key={size}
                    value={size}
                    control={<Radio />}
                    label={<Typography variant="body2">{size}</Typography>}
                  />
                ))}
              </RadioGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">Station status</FormLabel>
              <FormGroup>
                {["Operational", "Under maintenance", "Coming soon"].map(
                  (status) => (
                    <FormControlLabel
                      key={status}
                      control={
                        <Checkbox
                          checked={filterValues.stationStatus.includes(status)}
                          onChange={() => {
                            // Update filterValues immediately (no Apply button needed)
                            const currentStatus =
                              filterValues.stationStatus || [];
                            const isChecked = currentStatus.includes(status);

                            const updatedStatus = isChecked
                              ? currentStatus.filter((item) => item !== status)
                              : [...currentStatus, status];

                            // Update filterValues immediately to trigger map update
                            setFilterValues({
                              ...filterValues,
                              stationStatus: updatedStatus,
                            });

                            // Also update formik to keep form state in sync
                            formik.setFieldValue(
                              "stationStatus",
                              updatedStatus
                            );
                          }}
                        />
                      }
                      label={<Typography variant="body2">{status}</Typography>}
                    />
                  )
                )}
              </FormGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel component="legend">Hours</FormLabel>
              <FormGroup>
                {["24/7"].map((hour) => (
                  <FormControlLabel
                    key={hour}
                    control={
                      <Checkbox
                        checked={filterValues.hours.includes(hour)}
                        onChange={() => {
                          const currentHours = filterValues.hours || [];
                          const isChecked = currentHours.includes(hour);

                          const updatedHours = isChecked
                            ? currentHours.filter((item) => item !== hour)
                            : [...currentHours, hour];

                          setFilterValues({
                            ...filterValues,
                            hours: updatedHours,
                          });

                          formik.setFieldValue("hours", updatedHours);
                        }}
                      />
                    }
                    label={<Typography variant="body2">{hour}</Typography>}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <FormLabel className="payment_types" component="legend">
                Payment types
              </FormLabel>

              <FormControl className="payment-select" fullWidth>
                <InputLabel id="demo-multiple-checkbox-label">
                  Select all that apply
                </InputLabel>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  className="payment-selector-input"
                  multiple
                  value={
                    paymentValue.includes("Select All")
                      ? paymentMethods
                      : paymentValue
                  }
                  onChange={handleChange}
                  input={
                    <OutlinedInput
                      label={
                        <Typography variant="body2">
                          {/* DO NOT REMOVE THIS PLACEHOLDER*/}
                          -----------------
                        </Typography>
                      }
                    />
                  }
                  renderValue={(selected) =>
                    selected.includes("Select all")
                      ? "Select all"
                      : selected.join(", ")
                  }
                  MenuProps={MenuProps}
                >
                  <MenuItem value="Select all">
                    <Checkbox
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: { lg: 18, xs: 16 },
                          color: "#08652e",
                        },
                        paddingTop: "0",
                        paddingBottom: "0",
                      }}
                      checked={paymentValue.length === paymentMethods.length}
                    />
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{ fontSize: { xl: 14, xs: 12 } }}
                        >
                          Select all
                        </Typography>
                      }
                    />
                  </MenuItem>
                  {isLoadingPaymentMethods ? (
                    <MenuItem disabled>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">
                          Loading payment methods...
                        </Typography>
                      </Box>
                    </MenuItem>
                  ) : (
                    paymentMethods.map((name) => (
                      <MenuItem key={name} value={name}>
                        <Checkbox
                          className="selectOptionCheckbox"
                          sx={{
                            "& .MuiSvgIcon-root": {
                              fontSize: { lg: 18, xs: 16 },
                              color: "#08652e",
                            },
                            paddingTop: "0",
                            paddingBottom: "0",
                          }}
                          checked={paymentValue.indexOf(name) > -1}
                        />
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              sx={{ fontSize: { xl: 14, xs: 12 } }}
                            >
                              {name}
                            </Typography>
                          }
                        />
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </FormControl>

            <Box className="search-option-btn">
              <Button
                variant="outlined"
                color="secondary"
                type="reset"
                onClick={handleReset}
              >
                Reset
              </Button>
            </Box>
          </form>
        )}
      </Box>
      {/* {routeDetails && routeDetails.length > 0 && (
        <Box
          className='print-routes-main'
          style={{ top: '100px' }}
          onClick={function () {
            navigate('/printable-routes');
          }}
        >
          Print Routes
        </Box>
      )} */}
    </Box>
  );
};

export default SearchOptions;
